import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, PlayCircle, PartyPopper } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function LessonDetail() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentQuizIndex, setCurrentQuizIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [score, setScore] = useState(null);
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchLesson();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, i18n.language]);

  const fetchLesson = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/lessons/${id}`, {
        params: { language: i18n.language },
      });
      setLesson(response.data);
    } catch (error) {
      console.error('Failed to fetch lesson:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (quizId, answer) => {
    setAnswers({ ...answers, [quizId]: answer });
  };

  const handleSubmitQuiz = async () => {
    if (!lesson || lesson.quizzes.length === 0) return;

    const currentQuiz = lesson.quizzes[currentQuizIndex];
    const isCorrect = answers[currentQuiz.id] === currentQuiz.correctAnswer;

    let newScore = score || 0;
    if (isCorrect) {
      newScore += 1;
    }
    setScore(newScore);

    if (currentQuizIndex < lesson.quizzes.length - 1) {
      setCurrentQuizIndex(currentQuizIndex + 1);
    }

    if (currentQuizIndex === lesson.quizzes.length - 1) {
      await saveProgress(newScore);
    }
  };

  const saveProgress = async (finalScore) => {
    try {
      await axios.post(
        `${API_URL}/lessons/${id}/progress`,
        {
          isCompleted: true,
          score: finalScore,
          progress: 100,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
    } catch (error) {
      console.error('Failed to save progress:', error);
    }
  };

  if (loading) {
    return <div className="container mx-auto px-4 py-16 text-center text-stone-500 dark:text-stone-400">Loading...</div>;
  }

  if (!lesson) {
    return <div className="container mx-auto px-4 py-16 text-center text-stone-500 dark:text-stone-400">Lesson not found</div>;
  }

  const showQuiz = score === null && lesson.quizzes && lesson.quizzes.length > 0;
  const showResults = score !== null;

  return (
    <div className="container mx-auto px-4 py-10 max-w-3xl">
      <button
        onClick={() => navigate('/lessons')}
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-stone-500 dark:text-stone-400 hover:text-brand-700 dark:hover:text-brand-300 mb-4"
      >
        <ArrowLeft size={16} /> {t('lessons.title')}
      </button>
      <h1 className="font-display text-3xl font-semibold text-stone-800 dark:text-stone-100 mb-6">{lesson.title}</h1>

      <div className="bg-white dark:bg-stone-900 rounded-2xl border border-stone-200 dark:border-stone-800 p-8">
        {!showQuiz && !showResults && (
          <>
            <div className="mb-8">
              {typeof lesson.content === 'string' ? (
                <p className="text-stone-600 dark:text-stone-300 leading-relaxed">{lesson.content}</p>
              ) : Array.isArray(lesson.content?.items) ? (
                <ul className="space-y-3">
                  {lesson.content.items.map((item, i) => (
                    <li
                      key={i}
                      className="flex items-center justify-between bg-brand-50/60 dark:bg-brand-900/30 rounded-xl px-5 py-4"
                    >
                      <span className="font-arabic text-2xl text-brand-900 dark:text-brand-200">{item}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <pre className="text-sm">{JSON.stringify(lesson.content, null, 2)}</pre>
              )}
            </div>

            {lesson.videoUrl && (
              <div className="mb-8">
                <h2 className="font-display text-lg font-semibold text-stone-800 dark:text-stone-100 mb-3">Video</h2>
                <iframe
                  width="100%"
                  height="400"
                  className="rounded-xl"
                  src={lesson.videoUrl}
                  title="Lesson Video"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
            )}

            {lesson.quizzes && lesson.quizzes.length > 0 && (
              <button
                onClick={() => setCurrentQuizIndex(0)}
                className="inline-flex items-center gap-2 bg-brand-700 text-white px-6 py-3 rounded-full font-semibold hover:bg-brand-800 transition-colors"
              >
                <PlayCircle size={18} />
                Start Quiz ({lesson.quizzes.length} questions)
              </button>
            )}
          </>
        )}

        {showQuiz && (
          <div>
            <div className="mb-6">
              <p className="text-sm text-stone-500 dark:text-stone-400 mb-2">
                Question {currentQuizIndex + 1} of {lesson.quizzes.length}
              </p>
              <div className="w-full bg-stone-100 dark:bg-stone-800 rounded-full h-2">
                <div
                  className="bg-brand-600 h-2 rounded-full transition-all"
                  style={{
                    width: `${((currentQuizIndex + 1) / lesson.quizzes.length) * 100}%`,
                  }}
                ></div>
              </div>
            </div>

            <h2 className="font-display text-xl font-semibold text-stone-800 dark:text-stone-100 mb-6">
              {lesson.quizzes[currentQuizIndex].question}
            </h2>

            <div className="mb-6">
              {lesson.quizzes[currentQuizIndex].type === 'multiple_choice' && (
                <div className="space-y-2.5">
                  {lesson.quizzes[currentQuizIndex].options?.map((option, idx) => {
                    const checked = answers[lesson.quizzes[currentQuizIndex].id] === option;
                    return (
                      <label
                        key={idx}
                        className={`flex items-center gap-3 p-3.5 border rounded-xl cursor-pointer transition-colors ${
                          checked
                            ? 'border-brand-400 bg-brand-50 dark:bg-brand-900/30 dark:border-brand-600'
                            : 'border-stone-200 dark:border-stone-700 hover:bg-stone-50 dark:hover:bg-stone-800'
                        }`}
                      >
                        <input
                          type="radio"
                          name={`quiz-${lesson.quizzes[currentQuizIndex].id}`}
                          value={option}
                          checked={checked}
                          onChange={() =>
                            handleAnswerChange(lesson.quizzes[currentQuizIndex].id, option)
                          }
                          className="accent-brand-600"
                        />
                        <span className="font-arabic text-lg text-stone-700 dark:text-stone-200">{option}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            <button
              onClick={handleSubmitQuiz}
              disabled={!answers[lesson.quizzes[currentQuizIndex].id]}
              className="bg-brand-700 text-white px-6 py-3 rounded-full font-semibold hover:bg-brand-800 disabled:bg-stone-300 dark:disabled:bg-stone-700 disabled:cursor-not-allowed transition-colors"
            >
              {currentQuizIndex === lesson.quizzes.length - 1 ? 'Finish' : 'Next'}
            </button>
          </div>
        )}

        {showResults && (
          <div className="text-center py-4">
            <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-accent-100 dark:bg-accent-900/40 text-accent-600 dark:text-accent-300 mb-4">
              <PartyPopper size={26} />
            </div>
            <h2 className="font-display text-2xl font-semibold text-stone-800 dark:text-stone-100 mb-4">Quiz Complete!</h2>
            <div className="text-5xl font-semibold text-brand-700 dark:text-brand-300 mb-3">
              {score}/{lesson.quizzes.length}
            </div>
            <p className="text-stone-500 dark:text-stone-400 mb-8">
              You scored {Math.round((score / lesson.quizzes.length) * 100)}%
            </p>
            <button
              onClick={() => navigate('/lessons')}
              className="bg-brand-700 text-white px-6 py-3 rounded-full font-semibold hover:bg-brand-800 transition-colors"
            >
              Back to Lessons
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default LessonDetail;
